import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sweepUnresponsive, applyReengage } from '../services/crmService';
import { useNavigate } from 'react-router-dom';
import { useVisibleContacts } from '../pages/crm/useCrm';
import { usePipelineRows } from '../pages/crm/ContactExtras';
import QuickActions from '../pages/crm/QuickActions';
import PersonPeekPanel, { PersonName } from './personPeek/PersonPeekPanel';
import { todayET } from '../utils/dueDates';
import TaskKindLabel from './TaskKindLabel';

// Dashboard "Lead Tasks": open tasks linked to a lead/contact or to a buyer/seller pipeline record.
// Quick actions come from QuickActions, so Do Not Call, opt-outs and archived contacts are respected
// (calls use tel:, Send via Quo shows only for Kelly). Nothing is sent automatically.
export const isLeadTask = (t) => !!t && t.status !== 'completed' && !!(t.crmContactId || t.sellerInterestId || t.buyerInterestId);
const fmtDue = (t) => (t.dueDate ? `${t.dueDate}${t.dueTime ? ` ${t.dueTime}` : ''}` : 'No due date');

export default function LeadTasksPanel({ tasks, uid, isAdmin, senderName, onDone, onRefresh }) {
  const navigate = useNavigate();
  const contacts = useVisibleContacts({ includeArchived: true });
  const { sellers, buyers } = usePipelineRows();
  const [openId, setOpenId] = useState(null);
  const [peek, setPeek] = useState(null);
  const closePeek = useCallback(() => setPeek(null), []);
  const today = todayET();
  // Day 12 rule: tag leads #unresponsive when no contact was made (runs once per load; ship 1 has no server cron).
  const swept = useRef(false);
  useEffect(() => { if (!swept.current && uid && contacts.length) { swept.current = true; applyReengage(contacts, uid, isAdmin).finally(() => sweepUnresponsive(contacts, uid, isAdmin)); } }, [contacts, uid, isAdmin]);
  const rows = useMemo(() => (tasks || []).filter(isLeadTask).map((t) => {
    const rec = t.sellerInterestId ? sellers.find((r) => r.id === t.sellerInterestId) : t.buyerInterestId ? buyers.find((r) => r.id === t.buyerInterestId) : null;
    const cid = t.crmContactId || (rec && rec.contactId);
    const contact = cid ? contacts.find((c) => c.id === cid) : null;
    const who = contact ? ([contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name) : rec ? ([rec.firstName, rec.lastName].filter(Boolean).join(' ') || rec.name) : (t.crmContactName || '');
    const link = contact ? `/leads/contacts?id=${contact.id}` : rec ? `/pipeline?${t.buyerInterestId ? 'view=buyer&' : ''}open=${rec.id}` : null;
    return { t, contact, rec, who, link, kind: t.sellerInterestId ? 'Seller' : t.buyerInterestId ? 'Buyer' : 'Lead' };
  }).sort((a, b) => String(a.t.dueDate || '9999').localeCompare(String(b.t.dueDate || '9999'))), [tasks, contacts, sellers, buyers]);

  if (!rows.length) return <div style={{ color: '#7f7f7f', textAlign: 'center', padding: '1.5rem' }}>No open lead tasks.</div>;
  return (
    <div>
      <PersonPeekPanel person={peek} onClose={closePeek} />
      {rows.map(({ t, contact, who, link, kind }) => {
        const overdue = t.dueDate && t.dueDate < today;
        return (
          <div key={t.id} style={{ borderBottom: '1px solid #E1E4EA', padding: '0.6rem 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}><TaskKindLabel task={t} style={{ marginRight: 6 }} />{t.title || t.name || 'Task'}</div>
                <div style={{ fontSize: 13, color: overdue ? '#D64545' : '#7f7f7f' }}>{kind}{who ? ': ' : ''}{who && <PersonName onOpen={() => setPeek({ contactId: contact?.id || t.crmContactId, sellerInterestId: t.sellerInterestId, buyerInterestId: t.buyerInterestId, name: who })}>{who}</PersonName>} · {overdue ? 'Overdue ' : 'Due '}{fmtDue(t)}{contact && contact.archived ? ' · Archived' : ''}{contact && contact.dnc ? ' · On Do Not Call' : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {contact && <button type="button" style={btn} onClick={() => setOpenId(openId === t.id ? null : t.id)}>{openId === t.id ? 'Hide actions' : 'Call / Text / Email'}</button>}
                {link && <button type="button" style={btn} onClick={() => navigate(link)}>Open record</button>}
                <button type="button" style={{ ...btn, borderColor: '#D4AF37', fontWeight: 600 }} onClick={() => onDone && onDone(t.id)}>Mark done</button>
              </div>
            </div>
            {openId === t.id && contact && <div style={{ marginTop: 8 }}><QuickActions contact={contact} uid={uid} isAdmin={isAdmin} senderName={senderName} compact task={t} onTaskCleared={() => { setOpenId(null); onRefresh && onRefresh(); }} /></div>}
            {!contact && <div style={{ fontSize: 12, color: '#7f7f7f', marginTop: 4 }}>No contact linked yet: open the record to call or email.</div>}
          </div>);
      })}
    </div>
  );
}
const btn = { padding: '4px 10px', borderRadius: 6, border: '1px solid #b8a57a', background: '#fff', cursor: 'pointer', fontSize: 13 };
